import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useRegisterClubMutation } from '../api/apiSlice';
import { useCompleteLogin } from '../hooks/useCompleteLogin';

const PLAN_IDS = ['starter', 'club', 'league'] as const;
type PlanId = (typeof PLAN_IDS)[number];

const PLAN_PRICES: Record<PlanId, number | null> = {
    starter: null,
    club: 19,
    league: 49,
};

function RegisterClub() {
    const { t } = useTranslation();
    const [selectedPlan, setSelectedPlan] = useState<PlanId>('club');

    const [clubName, setClubName] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [name, setName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [passwordCheck, setPasswordCheck] = useState('');
    const [error, setError] = useState('');

    const [registerClub, { isLoading: isSubmitting }] = useRegisterClubMutation();
    const completeLogin = useCompleteLogin();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!clubName || !city || !name || !firstName || !email || !password) {
            setError(t('registerClub.fillAllFields'));
            return;
        }

        if (password !== passwordCheck) {
            setError(t('registerClub.passwordMismatch'));
            return;
        }

        try {
            const token = await registerClub({
                clubName,
                city,
                country: country || undefined,
                plan: selectedPlan,
                name,
                firstName,
                email,
                phone: phone || undefined,
                password,
            }).unwrap();

            setError('');
            completeLogin(token);
        } catch (err: any) {
            if (err?.data?.code === 'ACCOUNT_EXISTS') {
                setError(t('registerClub.accountExists'));
            } else {
                setError(t('registerClub.registrationFailed'));
            }
        }
    };

    return (
        <Box>
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
                <Typography variant="h4" component="h1" textAlign="center" fontWeight={700} gutterBottom>
                    {t('registerClub.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 1 }}>
                    {t('registerClub.subtitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 5 }}>
                    {t('registerClub.disclaimer')}
                </Typography>

                <Grid container spacing={4} alignItems="stretch" justifyContent="center">
                    {PLAN_IDS.map((planId) => {
                        const isSelected = planId === selectedPlan;
                        const price = PLAN_PRICES[planId];
                        const features = t(`registerClub.plans.${planId}.features`, { returnObjects: true }) as string[];

                        return (
                            <Grid item xs={12} sm={6} md={4} key={planId}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderWidth: isSelected ? 2 : 1,
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        position: 'relative',
                                    }}
                                >
                                    {planId === 'club' && (
                                        <Chip
                                            label={t('registerClub.mostPopular')}
                                            color="primary"
                                            size="small"
                                            sx={{ position: 'absolute', top: 16, right: 16 }}
                                        />
                                    )}
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="h6" fontWeight={700}>
                                            {t(`registerClub.plans.${planId}.name`)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {t(`registerClub.plans.${planId}.tagline`)}
                                        </Typography>
                                        <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                                            {price === null ? (
                                                t('registerClub.free')
                                            ) : (
                                                <>
                                                    {price}€
                                                    <Typography component="span" variant="body1" color="text.secondary">
                                                        {t('registerClub.perMonth')}
                                                    </Typography>
                                                </>
                                            )}
                                        </Typography>
                                        <List dense sx={{ flexGrow: 1 }}>
                                            {features.map((feature) => (
                                                <ListItem key={feature} disableGutters>
                                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                                        <CheckCircleIcon color="primary" fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={feature} />
                                                </ListItem>
                                            ))}
                                        </List>
                                        <Button
                                            variant={isSelected ? 'contained' : 'outlined'}
                                            color="primary"
                                            fullWidth
                                            sx={{ mt: 2 }}
                                            onClick={() => setSelectedPlan(planId)}
                                        >
                                            {isSelected ? t('registerClub.selectedPlan') : t('registerClub.selectPlan')}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Container>

            <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
                <Container maxWidth="sm">
                    <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
                        <Typography variant="h5" component="h2" fontWeight={700} gutterBottom textAlign="center">
                            {t('registerClub.formTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                            {t('registerClub.formSubtitle', { plan: t(`registerClub.plans.${selectedPlan}.name`) })}
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        label={t('registerClub.clubName')}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={clubName}
                                        onChange={(e) => setClubName(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label={t('registerClub.city')}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t('registerClub.country')}
                                        variant="outlined"
                                        fullWidth
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                                        {t('registerClub.adminSectionTitle')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label={t('registerClub.lastName')}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label={t('registerClub.firstName')}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        type="email"
                                        label={t('registerClub.email')}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t('registerClub.mobile')}
                                        variant="outlined"
                                        fullWidth
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        type="password"
                                        label={t('registerClub.password')}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        type="password"
                                        label={t('registerClub.confirmPassword')}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={passwordCheck}
                                        onChange={(e) => setPasswordCheck(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        color="button"
                                        fullWidth
                                        size="large"
                                        type="submit"
                                        disabled={isSubmitting}
                                        startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                                    >
                                        {t('registerClub.submit')}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                    <Link component={RouterLink} to="/login" variant="body2">
                                        {t('registerClub.hasAccount')}
                                    </Link>
                                </Grid>
                            </Grid>
                        </form>
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
}

export default RegisterClub;
